import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Challenge, UserChallengeProgress } from '../types';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import {
  activityMatchesChallengeRequirements,
  ChallengeMatchActivity,
} from './challengeActivityMatch';

function challengeAppliesToActivityCalendarDate(challenge: Challenge, activityDateYmd: string): boolean {
  const startYmd = firestoreTimestampToYmd(challenge.startDate);
  if (startYmd && activityDateYmd < startYmd) return false;
  const endYmd = challenge.endDate ? firestoreTimestampToYmd(challenge.endDate) : null;
  if (endYmd && activityDateYmd > endYmd) return false;
  return true;
}

function firestoreTimestampToYmd(ts: unknown): string | null {
  if (!ts) return null;
  const t = ts as { toDate?: () => Date; seconds?: number };
  if (typeof t.toDate === 'function') {
    return t.toDate().toISOString().slice(0, 10);
  }
  if (typeof t.seconds === 'number') {
    return new Date(t.seconds * 1000).toISOString().slice(0, 10);
  }
  return null;
}

export const challengeService = {
  // Create a new challenge
  async createChallenge(challenge: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const challengesCollection = collection(db, 'challenges');
      const challengeData = {
        ...challenge,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(challengesCollection, challengeData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating challenge:', error);
      throw error;
    }
  },

  // Get active challenges for a team
  async getActiveChallenges(teamId: string): Promise<Challenge[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'challenges',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Challenge[];
      }
      
      // On web/Android, use SDK
      const challengesCollection = collection(db, 'challenges');
      const q = query(
        challengesCollection,
        where('teamId', '==', teamId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Challenge[];
    } catch (error) {
      console.error('Error getting active challenges:', error);
      throw error;
    }
  },

  // Get user's progress on a specific challenge
  async getUserChallengeProgress(userId: string, challengeId: string): Promise<UserChallengeProgress | null> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const progressDoc = await firestoreRestApi.getDocument('userChallengeProgress', `${userId}_${challengeId}`);
        if (!progressDoc) return null;
        
        return {
          id: progressDoc.id,
          ...progressDoc.data()
        } as UserChallengeProgress;
      }
      
      // On web/Android, use SDK
      const progressRef = doc(db, 'userChallengeProgress', `${userId}_${challengeId}`);
      const progressDoc = await getDoc(progressRef);
      
      if (!progressDoc.exists()) {
        return null;
      }
      
      return {
        id: progressDoc.id,
        ...progressDoc.data()
      } as UserChallengeProgress;
    } catch (error) {
      console.error('Error getting user challenge progress:', error);
      throw error;
    }
  },

  // Update user's progress on a challenge
  async updateChallengeProgress(
    userId: string,
    challengeId: string,
    teamId: string,
    progress: number,
    isCompleted: boolean = false
  ): Promise<void> {
    try {
      const progressDocId = `${userId}_${challengeId}`;
      let wasCompleted = false;

      if (Capacitor.getPlatform() === 'ios') {
        const existing = await firestoreRestApi.getDocument('userChallengeProgress', progressDocId);
        const prev = existing?.data ? existing.data() : existing;
        wasCompleted = !!(prev?.isCompleted);

        const patch: Record<string, unknown> = {
          userId,
          challengeId,
          teamId,
          progress,
          isCompleted,
          updatedAt: new Date(),
        };
        if (!prev) {
          patch.createdAt = new Date();
        }
        if (isCompleted) {
          patch.completedAt = new Date();
          patch.pointsEarned = 1;
        }
        await firestoreRestApi.setDocument('userChallengeProgress', progressDocId, patch, true);
      } else {
        const progressRef = doc(db, 'userChallengeProgress', progressDocId);
        const snap = await getDoc(progressRef);
        wasCompleted = snap.exists() ? !!(snap.data() as UserChallengeProgress).isCompleted : false;

        const progressData: Record<string, unknown> = {
          userId,
          challengeId,
          teamId,
          progress,
          isCompleted,
          updatedAt: Timestamp.now(),
        };
        if (!snap.exists()) {
          progressData.createdAt = Timestamp.now();
        }
        if (isCompleted) {
          progressData.completedAt = Timestamp.now();
          progressData.pointsEarned = 1;
        }
        await setDoc(progressRef, progressData as Partial<UserChallengeProgress>, { merge: true });
      }

      if (isCompleted && !wasCompleted) {
        const { pointsService } = await import('./pointsService');
        await pointsService.awardChallengePoints(userId, teamId, 1, challengeId);
      }
    } catch (error) {
      console.error('Error updating challenge progress:', error);
      throw error;
    }
  },

  /**
   * After an activity is saved, advance any active team challenges whose requirements match.
   * Workout challenges match legacy runType Workout and Run + runSubType Workout (see challengeActivityMatch).
   */
  async processActivityForChallenges(
    userId: string,
    teamId: string,
    activity: ChallengeMatchActivity,
    activityDateYmd: string
  ): Promise<void> {
    try {
      const challenges = await this.getActiveChallenges(teamId);
      for (const challenge of challenges) {
        if (!challengeAppliesToActivityCalendarDate(challenge, activityDateYmd)) continue;
        if (!activityMatchesChallengeRequirements(activity, challenge.requirements)) continue;

        const existing = await this.getUserChallengeProgress(userId, challenge.id);
        if (existing?.isCompleted) continue;

        const current = existing?.progress ?? 0;

        if (challenge.type === 'daily') {
          await this.updateChallengeProgress(userId, challenge.id, teamId, 100, true);
          continue;
        }

        const step = challenge.type === 'weekly' ? 34 : 25;
        const next = Math.min(100, current + step);
        await this.updateChallengeProgress(userId, challenge.id, teamId, next, next >= 100);
      }
    } catch (e) {
      console.warn('processActivityForChallenges:', e);
    }
  },

  // Get all user's challenge progress for a team
  async getUserChallengesProgress(userId: string, teamId: string): Promise<UserChallengeProgress[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'userChallengeProgress',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        return documents.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserChallengeProgress[];
      }
      
      // On web/Android, use SDK
      const progressCollection = collection(db, 'userChallengeProgress');
      const q = query(
        progressCollection,
        where('userId', '==', userId),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserChallengeProgress[];
    } catch (error) {
      console.error('Error getting user challenges progress:', error);
      throw error;
    }
  }
};
